// The FanWave anchor grammar — mirrors the backend's `constants/fanwave.ts` (FanwaveAnchorKind,
// FanwaveMatchEvent, FANWAVE_MAX_ANCHOR_*) and `lib/fanwave/fanwaveAnchorValidation.ts`, so the
// CMS refuses an illegal pair before the API has to (the server re-validates either way).
//
// A wave is exactly TWO ANCHORS: when it opens, and when it closes, chosen independently. Every
// schedule the platform has ever shipped is a pair drawn from this closed set, which is the whole
// point of the model — the four ad-hoc shapes that grew over successive rounds of client feedback
// are four of the combinations, and the ones nobody has asked for yet cost nothing.

/** How one edge is expressed. Mirrors `FanwaveAnchorKind`. */
export const ANCHOR_KIND = {
  /** A match phase transition, plus an optional signed minute offset (`FT −10`, `HT +5`). */
  MATCH_EVENT: 'match_event',
  /** A minute on the live match clock. Coarser than an event — the clock stops and stretches. */
  MATCH_MINUTE: 'match_minute',
  /** A wall-clock date-time. Ignores the match entirely. */
  ABSOLUTE: 'absolute',
  /** Minutes after the wave actually OPENED. Close edge only — an open has nothing to follow. */
  DURATION: 'duration'
};

/** The closed set of phase transitions an anchor may name. Mirrors `FanwaveMatchEvent`. */
export const MATCH_EVENT = {
  KICKOFF: 'kickoff',
  HALF_TIME: 'half_time',
  SECOND_HALF: 'second_half',
  EXTRA_TIME: 'extra_time',
  PENALTIES: 'penalties',
  FULL_TIME: 'full_time'
};

/**
 * The events in the order a match reaches them — the order the phase strip draws them in.
 *
 * NOT the order their PROJECTED times sort into: before kickoff the backend projects extra-time
 * and full-time to the same instant and penalties to 30 minutes after both (see
 * `projectedOffsetMin`), so sorting purely by time would put full-time in the middle. This array
 * is the tie-break, and the reason `orderedPhases` needs one.
 */
export const MATCH_EVENT_ORDER = [
  MATCH_EVENT.KICKOFF,
  MATCH_EVENT.HALF_TIME,
  MATCH_EVENT.SECOND_HALF,
  MATCH_EVENT.EXTRA_TIME,
  MATCH_EVENT.PENALTIES,
  MATCH_EVENT.FULL_TIME
];

/** Full names, for dropdowns and prose. */
export const MATCH_EVENT_LABEL = {
  [MATCH_EVENT.KICKOFF]: 'Kick-off',
  [MATCH_EVENT.HALF_TIME]: 'Half-time',
  [MATCH_EVENT.SECOND_HALF]: 'Second-half kick-off',
  [MATCH_EVENT.EXTRA_TIME]: 'Extra time',
  [MATCH_EVENT.PENALTIES]: 'Penalties',
  [MATCH_EVENT.FULL_TIME]: 'Full-time'
};

/** Abbreviations for the segment labels under the track ("2H+6 → FT−10"). */
export const MATCH_EVENT_SHORT = {
  [MATCH_EVENT.KICKOFF]: 'KO',
  [MATCH_EVENT.HALF_TIME]: 'HT',
  [MATCH_EVENT.SECOND_HALF]: '2H',
  [MATCH_EVENT.EXTRA_TIME]: 'ET',
  [MATCH_EVENT.PENALTIES]: 'PENS',
  [MATCH_EVENT.FULL_TIME]: 'FT'
};

/** Validation bounds — mirror FANWAVE_MAX_ANCHOR_OFFSET_MIN / _DURATION_MIN and the minute range. */
export const ANCHOR_BOUNDS = {
  MAX_OFFSET_MIN: 240,
  MIN_MATCH_MINUTE: 0,
  MAX_MATCH_MINUTE: 150,
  MIN_DURATION_MIN: 1,
  MAX_DURATION_MIN: 4320
};

/**
 * How long Game Day Fan Wave 1 runs: it opens at Half-Time and closes this many minutes after it
 * actually opened. Fixed by the client's dual-wave brief — Wave 2 then takes over at the
 * second-half kick-off, so this is the length of the break the first wave is meant to fill.
 */
export const HALF_TIME_WAVE_MIN = 15;

/**
 * Which kinds each edge may use. `duration` is excluded from the OPEN edge because it measures
 * from the open, and an open cannot measure from itself — the API rejects it outright.
 */
export const OPEN_ANCHOR_KINDS = [
  ANCHOR_KIND.MATCH_EVENT,
  ANCHOR_KIND.MATCH_MINUTE,
  ANCHOR_KIND.ABSOLUTE
];
export const CLOSE_ANCHOR_KINDS = [
  ANCHOR_KIND.MATCH_EVENT,
  ANCHOR_KIND.MATCH_MINUTE,
  ANCHOR_KIND.ABSOLUTE,
  ANCHOR_KIND.DURATION
];

export const ANCHOR_KIND_LABEL = {
  [ANCHOR_KIND.MATCH_EVENT]: 'Match event',
  [ANCHOR_KIND.MATCH_MINUTE]: 'Match minute',
  [ANCHOR_KIND.ABSOLUTE]: 'Date & time',
  [ANCHOR_KIND.DURATION]: 'Duration after opening'
};

/** Which edge an anchor is — `duration` and the open/close validation rules depend on it. */
export const ANCHOR_EDGE = { OPEN: 'open', CLOSE: 'close' };

const MS_PER_MIN = 60 * 1000;

// A true minus sign, not a hyphen: "FT−10" is a signed quantity and reads as one at 11px.
const MINUS = '−';

/** Signed offset as it appears in a label: 0 → "", 6 → "+6", −10 → "−10". */
const offsetSuffix = (offsetMin) => {
  const n = Number(offsetMin);
  if (!Number.isFinite(n) || n === 0) return '';
  return n > 0 ? `+${n}` : `${MINUS}${Math.abs(n)}`;
};

/**
 * The blank editor state for one edge.
 *
 * Every payload field lives on the draft at once, and `kind` selects which one is live. Holding
 * them together is deliberate: an admin who flips the dropdown to compare "half-time" against a
 * date-time and flips back finds the value they typed still there. Nothing dead ever reaches the
 * wire, because `anchorPayload` emits ONLY the field belonging to the chosen kind — which is the
 * rule the API enforces (a `match_event` anchor carrying a `minute` is rejected, not ignored).
 */
export const emptyAnchorDraft = (kind = ANCHOR_KIND.MATCH_EVENT) => ({
  kind,
  event: MATCH_EVENT.HALF_TIME,
  offsetMin: '',
  minute: '',
  at: '',
  durationMin: '',
  // `duration` only: also close at this event if it comes first ('' = no cap).
  untilEvent: ''
});

/**
 * An anchor from the API (or a preset) as editor state.
 *
 * `at` arrives as an ISO instant and has to become a `datetime-local` string in the admin's own
 * timezone; the rest are numbers rendered as text so a half-typed "−" isn't coerced to 0.
 */
export const anchorToDraft = (anchor, toLocal) => {
  const base = emptyAnchorDraft(anchor?.kind || ANCHOR_KIND.MATCH_EVENT);
  if (!anchor) return base;
  return {
    ...base,
    kind: anchor.kind,
    event: anchor.event ?? base.event,
    offsetMin: anchor.offsetMin == null ? '' : String(anchor.offsetMin),
    minute: anchor.minute == null ? '' : String(anchor.minute),
    at: anchor.at ? (toLocal ? toLocal(anchor.at) : anchor.at) : '',
    durationMin: anchor.durationMin == null ? '' : String(anchor.durationMin),
    untilEvent: anchor.untilEvent ?? ''
  };
};

/**
 * The wire payload for one edge — ONLY the field its `kind` selects.
 *
 * This is where the API's strictest schedule rule is honoured: fields not belonging to the chosen
 * kind are REJECTED, not ignored, because a `match_event` anchor that also carries a `minute` is
 * almost certainly a client that changed the dropdown without clearing the old value, and two
 * plausible readings of one wave is a bug rather than a merge to guess at. Emitting from the kind
 * rather than clearing state on every dropdown change means there is no path where a stale field
 * survives to the request.
 *
 * `offsetMin` is omitted when zero — an anchor with no offset sits exactly on the event, and the
 * API treats an absent offset as exactly that.
 *
 * `toIso` converts the `datetime-local` string to the instant the API stores; pass the same
 * `fromDatetimeLocal` the rest of the form uses.
 */
export const anchorPayload = (draft, toIso) => {
  if (!draft?.kind) return null;

  if (draft.kind === ANCHOR_KIND.MATCH_EVENT) {
    const offset = Number(draft.offsetMin);
    const payload = { kind: ANCHOR_KIND.MATCH_EVENT, event: draft.event };
    if (draft.offsetMin !== '' && Number.isFinite(offset) && offset !== 0) {
      payload.offsetMin = offset;
    }
    return payload;
  }

  if (draft.kind === ANCHOR_KIND.MATCH_MINUTE) {
    return { kind: ANCHOR_KIND.MATCH_MINUTE, minute: Number(draft.minute) };
  }

  if (draft.kind === ANCHOR_KIND.ABSOLUTE) {
    const at = toIso ? toIso(draft.at) : draft.at;
    return { kind: ANCHOR_KIND.ABSOLUTE, at };
  }

  const payload = { kind: ANCHOR_KIND.DURATION, durationMin: Number(draft.durationMin) };
  if (draft.untilEvent) payload.untilEvent = draft.untilEvent;
  return payload;
};

/**
 * A draft as the plain anchor object the RESOLVERS take — `anchorPayload`'s sibling.
 *
 * The difference is the audience. `anchorPayload` builds the wire body, so it omits a zero offset
 * and formats the date-time as the instant the API stores. This one feeds the local resolver and
 * the preset matcher, so it keeps the offset explicit and returns null for an edge that is not
 * complete enough to place anywhere.
 */
export const anchorFromDraft = (draft) => {
  if (!draft?.kind) return null;
  if (draft.kind === ANCHOR_KIND.MATCH_EVENT) {
    return {
      kind: draft.kind,
      event: draft.event,
      offsetMin: draft.offsetMin === '' ? 0 : Number(draft.offsetMin)
    };
  }
  if (draft.kind === ANCHOR_KIND.MATCH_MINUTE) {
    return draft.minute === '' ? null : { kind: draft.kind, minute: Number(draft.minute) };
  }
  if (draft.kind === ANCHOR_KIND.ABSOLUTE) {
    return draft.at ? { kind: draft.kind, at: draft.at } : null;
  }
  if (draft.durationMin === '') return null;
  return {
    kind: draft.kind,
    durationMin: Number(draft.durationMin),
    ...(draft.untilEvent ? { untilEvent: draft.untilEvent } : {})
  };
};

/**
 * How one edge reads in a segment label: "HT", "2H+6", "FT−10", "46'", "20:45", "+20m".
 *
 * Compact by design — this sits under a track segment that may be 60px wide. `anchorSentence`
 * is the prose form for the pickers and the conflict copy.
 */
export const anchorLabel = (anchor, fmtTime) => {
  if (!anchor?.kind) return '—';
  switch (anchor.kind) {
    case ANCHOR_KIND.MATCH_EVENT:
      return `${MATCH_EVENT_SHORT[anchor.event] || anchor.event || '?'}${offsetSuffix(
        anchor.offsetMin
      )}`;
    case ANCHOR_KIND.MATCH_MINUTE:
      return anchor.minute == null ? '—' : `${anchor.minute}'`;
    case ANCHOR_KIND.ABSOLUTE:
      return anchor.at ? (fmtTime ? fmtTime(anchor.at) : String(anchor.at)) : '—';
    case ANCHOR_KIND.DURATION:
      if (anchor.durationMin == null) return '—';
      // "+15m/2H": fifteen minutes, or the second-half kick-off if that comes first.
      return anchor.untilEvent
        ? `+${anchor.durationMin}m/${MATCH_EVENT_SHORT[anchor.untilEvent] || anchor.untilEvent}`
        : `+${anchor.durationMin}m`;
    default:
      return '—';
  }
};

/** "2H+6 → FT−10" — both edges of a wave in one line. */
export const anchorPairLabel = (open, close, fmtTime) =>
  `${anchorLabel(open, fmtTime)} → ${anchorLabel(close, fmtTime)}`;

/** The prose form, for the pickers' summary line and the conflict explanations. */
export const anchorSentence = (anchor, fmtDateTime) => {
  if (!anchor?.kind) return 'Not set';
  switch (anchor.kind) {
    case ANCHOR_KIND.MATCH_EVENT: {
      const name = MATCH_EVENT_LABEL[anchor.event] || anchor.event;
      const n = Number(anchor.offsetMin);
      if (!Number.isFinite(n) || n === 0) return name;
      return n > 0 ? `${n} min after ${name}` : `${Math.abs(n)} min before ${name}`;
    }
    case ANCHOR_KIND.MATCH_MINUTE:
      return anchor.minute == null ? 'Not set' : `Match minute ${anchor.minute}`;
    case ANCHOR_KIND.ABSOLUTE:
      return anchor.at
        ? fmtDateTime
          ? fmtDateTime(anchor.at)
          : String(anchor.at)
        : 'Not set';
    case ANCHOR_KIND.DURATION:
      return anchor.durationMin == null
        ? 'Not set'
        : `${anchor.durationMin} min after the wave opens${
            anchor.untilEvent
              ? `, or at ${(MATCH_EVENT_LABEL[anchor.untilEvent] || anchor.untilEvent).toLowerCase()} if sooner`
              : ''
          }`;
    default:
      return 'Not set';
  }
};

/**
 * Does this edge sit on the MATCH clock — i.e. will its resolved time move as the match plays out?
 *
 * A `match_minute` edge is never reported as observed by the backend even once the match has
 * passed it (`resolveAnchor` hard-codes `observed: false`, because the wall-clock time it maps to
 * is reconstructed rather than recorded), so a wave carrying one stays `pending` for its whole
 * life. Worth saying out loud in the UI rather than letting it read as a stuck projection.
 */
export const isMatchClockAnchor = (anchor) =>
  anchor?.kind === ANCHOR_KIND.MATCH_EVENT || anchor?.kind === ANCHOR_KIND.MATCH_MINUTE;

/**
 * Validates ONE edge against the API's per-kind rules. Returns an error string, or null.
 *
 * `edge` matters for exactly one rule — `duration` is legal only on the close — but it is the
 * rule most likely to be reached by dragging, so it is checked here rather than left to the pair.
 */
export const validateAnchor = (draft, edge) => {
  if (!draft?.kind) return 'Pick how this edge is set';

  if (draft.kind === ANCHOR_KIND.DURATION && edge === ANCHOR_EDGE.OPEN) {
    return 'A duration measures from the open, so it cannot BE the open — pick an event, a match minute or a date & time';
  }

  switch (draft.kind) {
    case ANCHOR_KIND.MATCH_EVENT: {
      if (!MATCH_EVENT_ORDER.includes(draft.event)) return 'Pick a match event';
      if (draft.offsetMin === '' || draft.offsetMin == null) return null;
      const n = Number(draft.offsetMin);
      if (!Number.isInteger(n)) return 'Offset must be a whole number of minutes';
      if (Math.abs(n) > ANCHOR_BOUNDS.MAX_OFFSET_MIN) {
        return `Offset must be within ${ANCHOR_BOUNDS.MAX_OFFSET_MIN} minutes either side`;
      }
      return null;
    }
    case ANCHOR_KIND.MATCH_MINUTE: {
      if (draft.minute === '' || draft.minute == null) return 'Enter a match minute';
      const n = Number(draft.minute);
      if (!Number.isInteger(n)) return 'Match minute must be a whole number';
      if (n < ANCHOR_BOUNDS.MIN_MATCH_MINUTE || n > ANCHOR_BOUNDS.MAX_MATCH_MINUTE) {
        return `Match minute must be between ${ANCHOR_BOUNDS.MIN_MATCH_MINUTE} and ${ANCHOR_BOUNDS.MAX_MATCH_MINUTE}`;
      }
      return null;
    }
    case ANCHOR_KIND.ABSOLUTE: {
      if (!draft.at) return 'Pick a date & time';
      if (Number.isNaN(new Date(draft.at).getTime())) return 'That date & time is not valid';
      return null;
    }
    case ANCHOR_KIND.DURATION: {
      if (draft.durationMin === '' || draft.durationMin == null) return 'Enter a duration';
      const n = Number(draft.durationMin);
      if (!Number.isInteger(n)) return 'Duration must be a whole number of minutes';
      if (n < ANCHOR_BOUNDS.MIN_DURATION_MIN || n > ANCHOR_BOUNDS.MAX_DURATION_MIN) {
        return `Duration must be between ${ANCHOR_BOUNDS.MIN_DURATION_MIN} and ${ANCHOR_BOUNDS.MAX_DURATION_MIN} minutes`;
      }
      return null;
    }
    default:
      return 'Pick how this edge is set';
  }
};

/**
 * Validates the PAIR — the checks that need both edges, run after each edge passes on its own.
 *
 * Only the cases decidable from the anchors themselves are checked. Two edges on different
 * clocks (half-time → a date-time) cannot be ordered without the fixture's observed timeline, and
 * the timeline endpoint's resolved `opensAt`/`closesAt` is the honest place to catch those — see
 * `orderingError` in timeline.js, which compares the RESOLVED span instead of guessing here.
 *
 * Returns `{ open, close, pair }` where each is an error string or null.
 */
export const validateAnchorPair = (openDraft, closeDraft, { now = new Date() } = {}) => {
  const open = validateAnchor(openDraft, ANCHOR_EDGE.OPEN);
  const close = validateAnchor(closeDraft, ANCHOR_EDGE.CLOSE);
  if (open || close) return { open, close, pair: null };

  let pair = null;

  // Same clock on both edges — orderable without the fixture, so say so now rather than after a 400.
  if (
    openDraft.kind === ANCHOR_KIND.ABSOLUTE &&
    closeDraft.kind === ANCHOR_KIND.ABSOLUTE &&
    new Date(closeDraft.at).getTime() <= new Date(openDraft.at).getTime()
  ) {
    pair = 'The wave must close after it opens';
  } else if (
    openDraft.kind === ANCHOR_KIND.MATCH_MINUTE &&
    closeDraft.kind === ANCHOR_KIND.MATCH_MINUTE &&
    Number(closeDraft.minute) <= Number(openDraft.minute)
  ) {
    pair = 'The closing minute must be later than the opening minute';
  } else if (
    openDraft.kind === ANCHOR_KIND.MATCH_EVENT &&
    closeDraft.kind === ANCHOR_KIND.MATCH_EVENT
  ) {
    const openIdx = MATCH_EVENT_ORDER.indexOf(openDraft.event);
    const closeIdx = MATCH_EVENT_ORDER.indexOf(closeDraft.event);
    const openOffset = Number(openDraft.offsetMin) || 0;
    const closeOffset = Number(closeDraft.offsetMin) || 0;
    if (closeIdx < openIdx) {
      pair = `${MATCH_EVENT_LABEL[closeDraft.event]} comes before ${MATCH_EVENT_LABEL[openDraft.event]} — the wave would close before it opened`;
    } else if (closeIdx === openIdx && closeOffset <= openOffset) {
      // Same event on both edges: the offsets are all that separate them, so they must differ.
      pair = `Both edges sit on ${MATCH_EVENT_LABEL[openDraft.event]} — give the close a later offset`;
    }
  }

  // A close already in the past leaves a wave that can never open — the API refuses it outright
  // (`endsAt must be in the future`), so catch it here where the field is still on screen.
  if (
    !pair &&
    closeDraft.kind === ANCHOR_KIND.ABSOLUTE &&
    new Date(closeDraft.at).getTime() <= now.getTime()
  ) {
    pair = 'That closing time has already passed — pick one in the future';
  }

  return { open, close, pair };
};

/**
 * Presets — named workflows that seed BOTH edges at once.
 *
 * Deliberately a dropdown above the two pickers rather than separate modes: the pickers stay the
 * single way a schedule is expressed, and a preset is only a starting point the admin then edits.
 * That is also what keeps the presets reachable by DRAGGING (snap a handle to half-time and you
 * have built preset A yourself) instead of behind a mode switch.
 *
 * `seed({ postFtMinutes, at, openAt })` returns `{ open, close }` anchor objects. `at` is the
 * admin's chosen date-time for the presets that end on the wall clock, and `openAt` the one that
 * also STARTS there — both come from the track as a `datetime-local` string.
 *
 * The four lettered presets are the client's brief. The extras are combinations the anchor model
 * makes free; they are grouped separately so the named workflows stay findable.
 */
export const WAVE_PRESETS = [
  {
    key: 'A',
    group: 'core',
    label: `A · Half-time (${HALF_TIME_WAVE_MIN} min)`,
    /*
     * Game Day Fan Wave 1 in the client's dual-wave brief: opens at half-time and runs for a
     * FIXED 15 minutes, then Fan Wave 2 (preset B) takes over at the second-half kick-off.
     *
     * Closes on a duration, capped at the second-half kick-off: whichever comes first. Closing on
     * the restart alone kept the wave open for however long the feed kept reporting HT; the cap
     * keeps a short break from holding Fan Wave 2 back. The restart-only shape is still available
     * as `half_time_break` below, for waves already saved with it.
     */
    hint: `Fan Wave 1 — opens at half-time and runs for ${HALF_TIME_WAVE_MIN} minutes, or until the second-half kick-off if that comes first.`,
    seed: () => ({
      open: { kind: ANCHOR_KIND.MATCH_EVENT, event: MATCH_EVENT.HALF_TIME },
      // Capped at the restart so Fan Wave 2 is never held back by a short break.
      close: {
        kind: ANCHOR_KIND.DURATION,
        durationMin: HALF_TIME_WAVE_MIN,
        untilEvent: MATCH_EVENT.SECOND_HALF
      }
    })
  },
  {
    key: 'B',
    group: 'core',
    label: 'B · Second half start',
    hint: 'Fan Wave 2 — opens at the second-half kick-off, closes at a time you choose.',
    needsCloseAt: true,
    seed: ({ at }) => ({
      open: { kind: ANCHOR_KIND.MATCH_EVENT, event: MATCH_EVENT.SECOND_HALF },
      close: { kind: ANCHOR_KIND.ABSOLUTE, at }
    })
  },
  {
    key: 'C',
    group: 'core',
    label: 'C · Half-time, custom end',
    hint: 'Opens at half-time, closes at a time you choose.',
    needsCloseAt: true,
    seed: ({ at }) => ({
      open: { kind: ANCHOR_KIND.MATCH_EVENT, event: MATCH_EVENT.HALF_TIME },
      close: { kind: ANCHOR_KIND.ABSOLUTE, at }
    })
  },
  {
    key: 'D',
    group: 'core',
    label: 'D · Fully custom',
    hint: 'Both edges on the wall clock — the match is ignored.',
    needsOpenAt: true,
    needsCloseAt: true,
    seed: ({ openAt, at }) => ({
      open: { kind: ANCHOR_KIND.ABSOLUTE, at: openAt },
      close: { kind: ANCHOR_KIND.ABSOLUTE, at }
    })
  },
  {
    key: 'half_time_break',
    group: 'extra',
    label: 'Half-time break (until the restart)',
    hint: 'Opens at half-time, closes when the live match reports the second-half kick-off.',
    seed: () => ({
      open: { kind: ANCHOR_KIND.MATCH_EVENT, event: MATCH_EVENT.HALF_TIME },
      close: { kind: ANCHOR_KIND.MATCH_EVENT, event: MATCH_EVENT.SECOND_HALF }
    })
  },
  {
    key: 'first_half',
    group: 'extra',
    label: 'First half only',
    hint: 'Kick-off to half-time.',
    seed: () => ({
      open: { kind: ANCHOR_KIND.MATCH_EVENT, event: MATCH_EVENT.KICKOFF },
      close: { kind: ANCHOR_KIND.MATCH_EVENT, event: MATCH_EVENT.HALF_TIME }
    })
  },
  {
    key: 'half_time_to_post_ft',
    group: 'extra',
    label: 'Half-time to post-full-time',
    hint: 'Opens at half-time, closes at full-time plus the channel window — the platform default.',
    seed: ({ postFtMinutes }) => ({
      open: { kind: ANCHOR_KIND.MATCH_EVENT, event: MATCH_EVENT.HALF_TIME },
      close: {
        kind: ANCHOR_KIND.MATCH_EVENT,
        event: MATCH_EVENT.FULL_TIME,
        offsetMin: Number(postFtMinutes) || 0
      }
    })
  },
  {
    key: 'pre_match',
    group: 'extra',
    label: 'Pre-match build-up',
    hint: '30 minutes before kick-off, until kick-off.',
    seed: () => ({
      open: { kind: ANCHOR_KIND.MATCH_EVENT, event: MATCH_EVENT.KICKOFF, offsetMin: -30 },
      close: { kind: ANCHOR_KIND.MATCH_EVENT, event: MATCH_EVENT.KICKOFF }
    })
  },
  {
    key: 'final_ten',
    group: 'extra',
    label: 'Final ten minutes',
    hint: 'Ten minutes before the whistle, until 30 minutes after it.',
    seed: () => ({
      open: { kind: ANCHOR_KIND.MATCH_EVENT, event: MATCH_EVENT.FULL_TIME, offsetMin: -10 },
      close: { kind: ANCHOR_KIND.MATCH_EVENT, event: MATCH_EVENT.FULL_TIME, offsetMin: 30 }
    })
  },
  {
    key: 'fixed_burst',
    group: 'extra',
    label: 'Fixed burst (20 min)',
    hint: 'Keeps whichever open is set and closes 20 minutes after the wave actually opens.',
    keepsOpen: true,
    seed: ({ open }) => ({
      // The one preset that PRESERVES the open edge: a duration is defined relative to it, so
      // replacing the open would change what the 20 minutes are measured from.
      open: open || { kind: ANCHOR_KIND.MATCH_EVENT, event: MATCH_EVENT.HALF_TIME },
      close: { kind: ANCHOR_KIND.DURATION, durationMin: 20 }
    })
  }
];

export const presetByKey = (key) => WAVE_PRESETS.find((p) => p.key === key) || null;

/**
 * Which preset a pair of anchors IS, or null for a hand-built schedule.
 *
 * Compared on the wire payload rather than the draft so a preset stays recognised after a
 * round-trip through `anchorToDraft` (where an absent offset becomes '' and back). The presets
 * that carry an admin-chosen date-time are matched on SHAPE only — the date itself is the
 * admin's, so requiring it to match would mean no preset ever recognised itself.
 */
export const matchPreset = (open, close, { postFtMinutes } = {}) => {
  if (!open || !close) return null;
  const shapeOf = (a) =>
    a.kind === ANCHOR_KIND.MATCH_EVENT
      ? `${a.kind}:${a.event}:${Number(a.offsetMin) || 0}`
      : a.kind === ANCHOR_KIND.ABSOLUTE
        ? a.kind
        : a.kind === ANCHOR_KIND.DURATION
          ? `${a.kind}:${a.durationMin}:${a.untilEvent || ''}`
          : `${a.kind}:${a.minute}`;

  const actual = `${shapeOf(open)}|${shapeOf(close)}`;
  const found = WAVE_PRESETS.find((preset) => {
    const seeded = preset.seed({ postFtMinutes, at: '', openAt: '', open });
    return `${shapeOf(seeded.open)}|${shapeOf(seeded.close)}` === actual;
  });
  return found || null;
};

/**
 * A wave's anchors as the CMS can best know them, and how much to trust the answer.
 *
 * No admin READ endpoint returns `openAnchor`/`closeAnchor` today: POST /create and PUT /update
 * echo the pair they were sent, but GET /list and GET /timeline report only the resolved
 * `opensAt`/`closesAt` and the derived legacy fields. So for a wave the CMS did not just write,
 * the anchors have to be reconstructed from those legacy fields — using the SAME mapping the
 * backend falls back to (`legacyToAnchors`), which is what makes the reconstruction agree with
 * how the wave is actually driven.
 *
 * That mapping is lossy in one direction the UI must not hide: `anchorsToLegacy` nulls every
 * event other than half-time (open) and full-time (close), and every non-zero offset and duration
 * with it. A wave created with `second_half +0` therefore reads back as `half_time +0`. Hence
 * `exact`: false means "this is the legacy projection, not the pair on file", and the caller is
 * expected to mark the label rather than present the guess as fact — the same rule
 * `scheduleUnrecorded` already applies to archived runs.
 *
 * `cached` is the create/update echo, held by the caller for the session; it is exact.
 */
export const readWaveAnchors = (wave, { postFtMinutes, cached } = {}) => {
  if (cached?.openAnchor && cached?.closeAnchor) {
    return {
      open: cached.openAnchor,
      close: cached.closeAnchor,
      exact: true,
      openExact: true,
      closeExact: true
    };
  }
  if (wave?.openAnchor && wave?.closeAnchor) {
    return {
      open: wave.openAnchor,
      close: wave.closeAnchor,
      exact: true,
      openExact: true,
      closeExact: true
    };
  }

  // Provenance is PER EDGE, because the legacy fields prove the two edges independently. A wave
  // stored as `startMinute: 46, endsAt: null` proves its open and says nothing about its close, and
  // treating the pair as one unit would either throw away the half that is known or present the
  // half that is not as fact.
  //
  // An edge is proven only when its own legacy field is set. Where it is null, `legacyToAnchors`
  // supplies the DEFAULT (half-time open, full-time + window close) — which is right for a wave
  // that really is on the default schedule and pure fiction for an anchor wave whose event or
  // duration the legacy fields cannot express. The two are indistinguishable from a list row, so
  // the caller must not render an unproven edge in anchor terms; it has the resolved
  // `opensAt`/`closesAt` from the timeline endpoint, which is true either way.
  return {
    ...legacyToAnchors(wave, postFtMinutes),
    exact: false,
    openExact: wave?.startsAt != null || wave?.startMinute != null,
    closeExact: wave?.endsAt != null || wave?.endMinute != null
  };
};

/**
 * One edge of a wave as it should READ on the track — the anchor when that edge is proven, and the
 * resolved wall-clock time when it is not.
 *
 * This is the rule that stops the track contradicting itself. Three waves with different schedules
 * whose legacy fields are all null every resolve to the same fabricated "HT → FT+30", which is
 * visibly false the moment they are drawn as three separate segments. Falling back to the resolved
 * time says less and is always true.
 *
 * `observed` drives the "~": a projected time will move.
 */
export const edgeLabel = ({ anchor, exact, at, observed, fmtTime }) => {
  if (exact) return anchorLabel(anchor, fmtTime);
  const time = fmtTime ? fmtTime(at) : String(at);
  return observed ? time : `~${time}`;
};

/** Both edges, each by its own provenance: "KO → HT", "19:34 → ~19:54", "~17:03 → ~17:48". */
export const waveEdgesLabel = (anchors, wave, fmtTime) => {
  if (!anchors) return '—';
  const observed = !wave?.pending;
  return `${edgeLabel({
    anchor: anchors.open,
    exact: anchors.openExact,
    at: wave?.opensAt,
    observed,
    fmtTime
  })} → ${edgeLabel({
    anchor: anchors.close,
    exact: anchors.closeExact,
    at: wave?.closesAt,
    observed,
    fmtTime
  })}`;
};

/**
 * Any pre-anchor wave as an anchor pair — a direct mirror of the backend's `legacyToAnchors`,
 * kept in step with it so the CMS orders and labels a legacy wave exactly as the API drives it.
 *
 *   default        HT +0     → FT +postFtMinutes
 *   match minutes  minute N  → minute M
 *   clock window   absolute  → absolute
 *   half-time end  HT +0     → absolute
 *   mixed          minute N  → absolute
 */
export const legacyToAnchors = (wave, postFtMinutes) => {
  const open =
    wave?.startsAt != null
      ? { kind: ANCHOR_KIND.ABSOLUTE, at: wave.startsAt }
      : wave?.startMinute != null
        ? { kind: ANCHOR_KIND.MATCH_MINUTE, minute: wave.startMinute }
        : { kind: ANCHOR_KIND.MATCH_EVENT, event: MATCH_EVENT.HALF_TIME };

  const close =
    wave?.endsAt != null
      ? { kind: ANCHOR_KIND.ABSOLUTE, at: wave.endsAt }
      : wave?.endMinute != null
        ? { kind: ANCHOR_KIND.MATCH_MINUTE, minute: wave.endMinute }
        : {
            kind: ANCHOR_KIND.MATCH_EVENT,
            event: MATCH_EVENT.FULL_TIME,
            offsetMin: Number(postFtMinutes) || 0
          };

  return { open, close };
};

/** Minutes → ms, for the callers that place an offset on the wall clock. */
export const offsetMs = (minutes) => (Number(minutes) || 0) * MS_PER_MIN;
